import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  ArrowLeft, Download, Upload, Link as LinkIcon, FileText, 
  MessageSquare, CheckCircle2, Clock, AlertCircle, Calendar,
  Car, Gauge, Fuel, Hash, Edit2, Check, X
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ClientSidebar from '../Sidebar/ClientSidebar';
import EngineerSidebar from '../Sidebar/EngineerSidebar';
import ChatBox from '../Chat/ChatBox';
import { parseUTC } from '../../lib/date';

const EngineerOrderDetail = ({ user }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(true); // Chat ouvert par défaut pour les ingénieurs
  const [diagnosticLink, setDiagnosticLink] = useState('');
  const [diagnosticFile, setDiagnosticFile] = useState(null);
  const [uploadingLog, setUploadingLog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [editingLogId, setEditingLogId] = useState(null);
  const [editingLogName, setEditingLogName] = useState('');
  const [services, setServices] = useState([]);
  const [optionsData, setOptionsData] = useState([]);

  useEffect(() => {
    loadOrderDetails();
    loadLogs();
    loadServicesAndOptions();
  }, [orderId]);

  const loadServicesAndOptions = async () => {
    try {
      const [servicesRes, optionsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/services`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/options`)
      ]);
      const servicesData = await servicesRes.json();
      const optionsDataRes = await optionsRes.json();
      setServices(servicesData);
      setOptionsData(optionsDataRes);
    } catch (error) {
      console.error('Error loading services/options:', error);
    }
  };

  const loadOrderDetails = async () => {
    try {
      const { ordersAPI } = await import('../../api');
      const data = await ordersAPI.getOrder(orderId);
      setOrder(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les détails de la commande",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const { ordersAPI } = await import('../../api');
      const logsData = await ordersAPI.getOrderLogs(orderId);
      setLogs(logsData);
    } catch (error) {
      console.error('Error loading logs:', error);
      // Ne pas afficher d'erreur si les logs n'existent pas encore
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDiagnosticFile(file);
    toast({
      title: "Fichier sélectionné",
      description: `${file.name} prêt à être uploadé`,
    });
  };

  const handleAddLog = async () => {
    if (!diagnosticLink.trim() && !diagnosticFile) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un fichier ou entrer un lien",
        variant: "destructive"
      });
      return;
    }

    setUploadingLog(true);

    try {
      const { ordersAPI } = await import('../../api');
      
      if (diagnosticFile) {
        // Upload file
        await ordersAPI.uploadLog(orderId, diagnosticFile);
        toast({
          title: "Log ajouté",
          description: "Le fichier de diagnostic a été uploadé",
        });
      } else if (diagnosticLink.trim()) {
        // Add link
        await ordersAPI.addLogLink(orderId, diagnosticLink);
        toast({
          title: "Lien ajouté",
          description: "Le lien de diagnostic a été ajouté",
        });
      }

      // Reset and reload
      setDiagnosticLink('');
      setDiagnosticFile(null);
      await loadLogs();
      
    } catch (error) {
      console.error('Error adding log:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'ajouter le log",
        variant: "destructive"
      });
    } finally {
      setUploadingLog(false);
    }
  };

  const handleDownloadLog = async (logId, fileName) => {
    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.downloadLog(orderId, logId);
      toast({
        title: "Téléchargement démarré",
        description: `${fileName} est en cours de téléchargement`,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  const handleRenameLog = async (logId, newName) => {
    if (!newName.trim()) {
      toast({
        title: "Erreur",
        description: "Le nom ne peut pas être vide",
        variant: "destructive"
      });
      return;
    }

    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.renameLog(orderId, logId, newName);
      toast({
        title: "Succès",
        description: "Le log a été renommé",
      });
      setEditingLogId(null);
      setEditingLogName('');
      await loadLogs();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de renommer le log",
        variant: "destructive"
      });
    }
  };

  const handleDownloadFile = async (fileType) => {
    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.downloadFile(orderId, fileType);
      toast({
        title: "Téléchargement démarré",
        description: `Le fichier ${fileType} est en cours de téléchargement`,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  const handleModifiedFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const { ordersAPI } = await import('../../api');
      const result = await ordersAPI.uploadModifiedFile(orderId, file);
      
      toast({
        title: "Fichier uploadé",
        description: `${result.version} uploadé avec succès (${result.total_versions} versions au total)`,
      });

      // Recharger les détails de la commande
      await loadOrderDetails();
      
      // Réinitialiser l'input
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading modified file:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'uploader le fichier",
        variant: "destructive"
      });
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.updateOrderStatus(orderId, { status: newStatus });
      
      const statusLabels = {
        'in_progress': 'En cours',
        'pending': 'En attente de log',
        'completed': 'Terminé'
      };

      toast({
        title: "Statut mis à jour",
        description: `La commande est maintenant : ${statusLabels[newStatus]}`,
      });

      // Recharger les détails de la commande
      await loadOrderDetails();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de changer le statut",
        variant: "destructive"
      });
    }
  };

  const handleMarkOrderAsPaid = async () => {
    if (!window.confirm("Confirmer que le paiement a bien été reçu sur Stripe ?")) return;
    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.updateOrderStatus(orderId, { payment_status: "paid", status: "in_progress" });
      toast({
        title: "Succès",
        description: "Paiement confirmé manuellement",
      });
      await loadOrderDetails();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de confirmer le paiement",
        variant: "destructive"
      });
    }
  };

  const handleAddDiagnosticLink = () => {
    if (!diagnosticLink.trim()) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer un lien valide",
        variant: "destructive"
      });
      return;
    }

    handleAddLog();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-lg px-4 py-2">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Terminé
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30 text-lg px-4 py-2">
            <Clock className="h-4 w-4 mr-2" />
            En cours
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30 text-lg px-4 py-2">
            <AlertCircle className="h-4 w-4 mr-2" />
            En attente
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30 text-lg px-4 py-2">
            {status}
          </Badge>
        );
    }
  };

  const getServiceName = (serviceKey) => {
    const service = services.find(s => s.key === serviceKey);
    return service ? service.name : serviceKey;
  };

  const getOptionName = (optionKey) => {
    const option = optionsData.find(o => o.key === optionKey);
    return option ? option.name : optionKey;
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <EngineerSidebar />
        <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
          <div className="text-center py-12">
            <p className="text-[#A6A6A6]">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <EngineerSidebar />
        <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
          <div className="text-center py-12">
            <p className="text-[#A6A6A6]">Commande non trouvée</p>
            <Button onClick={() => navigate('/engineer/dashboard')} className="mt-4 bg-[#C00000]">
              Retour au dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <EngineerSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Button
            onClick={() => navigate('/engineer/dashboard')}
            variant="outline"
            className="mb-6 border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux commandes
          </Button>

          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  {order.vehicle_info.brand.toUpperCase()} {order.vehicle_info.model.toUpperCase()}
                </h1>
                <p className="text-[#A6A6A6] text-lg">
                  {order.vehicle_info.year} • {order.vehicle_info.engine}
                </p>
              </div>
              <div className="flex gap-3">
                {order.modified_file_name && (
                  <Button
                    onClick={() => handleDownloadFile('modified')}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger V1
                  </Button>
                )}
                <Button
                  onClick={() => setShowChat(true)}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Ouvrir un SAV
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - 2/3 width */}
            <div className="lg:col-span-2 space-y-6">
              {/* Options de Tuning */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Gauge className="h-5 w-5 text-[#C00000]" />
                    OPTIONS DE TUNING
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Badge className="bg-yellow-600/20 text-yellow-500 border-yellow-600/30 px-4 py-2 text-sm">
                      {getServiceName(order.service)}
                    </Badge>
                    {order.options && order.options.length > 0 && order.options.map((opt, index) => (
                      <Badge key={index} className="bg-yellow-600/20 text-yellow-500 border-yellow-600/30 px-4 py-2 text-sm">
                        {getOptionName(opt)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Liens Log / Diagnostic */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-white flex items-center gap-2">
                      <LinkIcon className="h-5 w-5 text-[#C00000]" />
                      LIENS LOG
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Upload File */}
                    <div className="space-y-2">
                      <Label className="text-[#A6A6A6] text-sm">Fichier de diagnostic</Label>
                      <div className="flex gap-3">
                        <Input
                          type="file"
                          onChange={handleFileSelect}
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                          accept=".bin,.log,.txt,.csv,.pdf"
                        />
                        <Upload className="h-5 w-5 text-[#C00000] flex-shrink-0 mt-2" />
                      </div>
                      {diagnosticFile && (
                        <p className="text-xs text-green-500">✓ {diagnosticFile.name}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="h-px flex-1 bg-[#A6A6A6]/20"></div>
                      <span className="text-[#A6A6A6] text-sm">OU</span>
                      <div className="h-px flex-1 bg-[#A6A6A6]/20"></div>
                    </div>

                    {/* Add Link */}
                    <div className="space-y-2">
                      <Label className="text-[#A6A6A6] text-sm">Lien vers le diagnostic</Label>
                      <div className="flex gap-3">
                        <Input
                          value={diagnosticLink}
                          onChange={(e) => setDiagnosticLink(e.target.value)}
                          placeholder="https://..."
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleAddLog}
                      disabled={uploadingLog || (!diagnosticFile && !diagnosticLink.trim())}
                      className="w-full bg-[#C00000] hover:bg-[#C00000]/90 disabled:opacity-50"
                    >
                      {uploadingLog ? 'Ajout en cours...' : '+ Ajouter'}
                    </Button>

                    {/* List of logs */}
                    {logs.length > 0 ? (
                      <div className="space-y-2 pt-4 border-t border-[#A6A6A6]/20">
                        {logs.map((log, index) => (
                          <div key={log.id || index} className="flex justify-between items-center p-3 bg-[#111111] rounded-lg">
                            <div className="flex items-center gap-3 flex-1">
                              {log.type === 'file' ? (
                                <FileText className="h-4 w-4 text-[#C00000] flex-shrink-0" />
                              ) : (
                                <LinkIcon className="h-4 w-4 text-[#C00000] flex-shrink-0" />
                              )}
                              <div className="flex-1">
                                {editingLogId === log.id ? (
                                  <div className="flex gap-2 items-center">
                                    <Input
                                      value={editingLogName}
                                      onChange={(e) => setEditingLogName(e.target.value)}
                                      className="bg-[#0a0a0a] border-[#C00000] text-white h-8 text-sm"
                                      placeholder={`V${index + 1}`}
                                      autoFocus
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleRenameLog(log.id, editingLogName)}
                                      className="bg-green-500 hover:bg-green-600 h-8 px-2"
                                    >
                                      <Check className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        setEditingLogId(null);
                                        setEditingLogName('');
                                      }}
                                      variant="outline"
                                      className="border-[#A6A6A6] text-white h-8 px-2"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-white font-semibold">
                                      {log.custom_name || `V${index + 1}`}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setEditingLogId(log.id);
                                        setEditingLogName(log.custom_name || `V${index + 1}`);
                                      }}
                                      className="h-6 w-6 p-0 text-[#A6A6A6] hover:text-white hover:bg-[#A6A6A6]/10"
                                    >
                                      <Edit2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )}
                                <p className="text-[#A6A6A6] text-xs">
                                  {log.type === 'file' ? log.filename : 'Lien externe'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {log.type === 'file' ? (
                                <Button
                                  onClick={() => handleDownloadLog(log.id, log.filename)}
                                  size="sm"
                                  variant="outline"
                                  className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => window.open(log.url, '_blank')}
                                  size="sm"
                                  variant="outline"
                                  className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                                >
                                  Ouvrir
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#A6A6A6] text-sm text-center py-2">Aucun log ajouté.</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Fichiers Originaux */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Upload className="h-5 w-5 text-[#C00000]" />
                    FICHIER ORIGINAL (ECU)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {order.original_file_name ? (
                      <div className="flex justify-between items-center p-3 bg-[#111111] rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 text-[#C00000]" />
                          <span className="text-white text-sm font-mono">{order.original_file_name}</span>
                        </div>
                        <Button
                          onClick={() => handleDownloadFile('original')}
                          size="sm"
                          variant="outline"
                          className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Télécharger
                        </Button>
                      </div>
                    ) : (
                      <p className="text-[#A6A6A6] text-sm text-center py-2">En attente du fichier client.</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Fichiers Modifiés (Multi-versions) */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Download className="h-5 w-5 text-green-500" />
                        FICHIERS MODIFIÉS
                      </CardTitle>
                      <p className="text-[#A6A6A6] text-sm mt-1">Versions du fichier ECU optimisé</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Liste des versions existantes */}
                    {order.modified_files && order.modified_files.length > 0 ? (
                      order.modified_files.map((modFile, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="h-4 w-4 text-green-500" />
                            <div>
                              <span className="text-white font-semibold block">{modFile.version}</span>
                              <span className="text-[#A6A6A6] text-xs">{modFile.filename}</span>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleDownloadFile('modified')}
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            {modFile.version}
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-[#A6A6A6] text-sm">Aucun fichier modifié uploadé</p>
                      </div>
                    )}

                    {/* Bouton d'upload pour nouvelle version */}
                    <div className="pt-3 border-t border-[#A6A6A6]/20">
                      <input
                        type="file"
                        id="modified-file-upload"
                        className="hidden"
                        accept=".bin,.hex,.kess,.ori"
                        onChange={handleModifiedFileUpload}
                      />
                      <Button
                        onClick={() => document.getElementById('modified-file-upload').click()}
                        className="w-full bg-green-500 hover:bg-green-600 text-white"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Uploader une nouvelle version (V{(order.modified_files?.length || 0) + 1})
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Chat visible et infos */}
            <div className="space-y-6">
              {/* Chat SAV - Visible par défaut */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-[#C00000]" />
                    CHAT SAV
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="h-[400px]">
                    <ChatBox 
                      orderId={order.order_id}
                      userRole="engineer"
                      onClose={() => {}} 
                      embedded={true}
                    />
                  </div>
                </CardContent>
              </Card>
              {/* Détails Véhicule (regroupe l'ancienne carte Information) */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Car className="h-5 w-5 text-[#C00000]" />
                      DÉTAILS VÉHICULE
                    </CardTitle>
                    <span className="text-[#A6A6A6] text-sm">
                      {parseUTC(order.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Infos commande */}
                  <div className="pb-3 border-b border-[#A6A6A6]/20 space-y-3">
                    <div>
                      <Label className="text-[#A6A6A6] text-xs">Numéro de commande</Label>
                      <p className="text-white font-mono">{order.order_id}</p>
                    </div>
                    <div>
                      <Label className="text-[#A6A6A6] text-xs">Client</Label>
                      <p className="text-white">{order.client_name}</p>
                    </div>
                    <div>
                      <Label className="text-[#A6A6A6] text-xs">Statut</Label>
                      <div className="mt-1">{getStatusBadge(order.status)}</div>
                    </div>
                    <div>
                      <Label className="text-[#A6A6A6] text-xs">Statut du paiement</Label>
                      <div className="mt-1">
                        {order.payment_status === 'paid' ? (
                          <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Payé
                          </Badge>
                        ) : order.payment_status === 'pending' ? (
                          <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">
                            <Clock className="h-3 w-3 mr-1" />
                            En attente
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Non payé
                          </Badge>
                        )}
                      </div>
                      {order.payment_status !== 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 border-green-600 text-green-500 hover:bg-green-600/10 w-full"
                          onClick={handleMarkOrderAsPaid}
                          data-testid="mark-order-paid-btn"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Marquer comme payé
                        </Button>
                      )}
                    </div>

                    {/* Boutons de changement de statut */}
                    <div className="pt-2">
                      <Label className="text-[#A6A6A6] text-xs mb-2 block">Changer le statut</Label>
                      <div className="flex flex-col gap-2">
                        {order.status !== 'in_progress' && (
                          <Button
                            onClick={() => handleStatusChange('in_progress')}
                            size="sm"
                            variant="outline"
                            className="border-blue-500 text-blue-500 hover:bg-blue-500/10 w-full"
                          >
                            <Clock className="h-3 w-3 mr-2" />
                            Marquer en cours
                          </Button>
                        )}
                        {order.status !== 'pending' && (
                          <Button
                            onClick={() => handleStatusChange('pending')}
                            size="sm"
                            variant="outline"
                            className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/10 w-full"
                          >
                            <AlertCircle className="h-3 w-3 mr-2" />
                            En attente de log
                          </Button>
                        )}
                        {order.status !== 'completed' && (
                          <Button
                            onClick={() => handleStatusChange('completed')}
                            size="sm"
                            variant="outline"
                            className="border-green-500 text-green-500 hover:bg-green-500/10 w-full"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-2" />
                            Marquer terminé
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="bg-[#C00000]/10 border border-[#C00000]/30 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[#A6A6A6]">Prix</span>
                        <span className="text-2xl font-bold text-[#C00000]">{order.price}€</span>
                      </div>
                    </div>
                  </div>

                  {/* Détails véhicule */}
                  {order.vehicle_info.category && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Catégorie</span>
                      <span className="text-white font-semibold">{order.vehicle_info.category}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#A6A6A6]">Marque</span>
                    <span className="text-white font-semibold">{order.vehicle_info.brand}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A6A6A6]">Modèle</span>
                    <span className="text-white font-semibold">{order.vehicle_info.model}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A6A6A6]">Année</span>
                    <span className="text-white font-semibold">{order.vehicle_info.year || 'N/A'}</span>
                  </div>
                  {order.vehicle_info.mileage && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Kilométrage</span>
                      <span className="text-white font-semibold">{order.vehicle_info.mileage}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#A6A6A6]">Moteur</span>
                    <span className="text-white font-semibold">{order.vehicle_info.engine || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A6A6A6]">Code Moteur</span>
                    <span className="text-white font-semibold">{order.vehicle_info.engine_code || 'N/A'}</span>
                  </div>
                  {order.vehicle_info.power && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Puissance</span>
                      <span className="text-white font-semibold">{order.vehicle_info.power} CV</span>
                    </div>
                  )}
                  {order.vehicle_info.fuel && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Carburant</span>
                      <span className="text-white font-semibold">{order.vehicle_info.fuel}</span>
                    </div>
                  )}
                  {order.vehicle_info.gearbox && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Boîte</span>
                      <span className="text-white font-semibold">{order.vehicle_info.gearbox}</span>
                    </div>
                  )}
                  {order.vehicle_info.transmission && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Transmission</span>
                      <span className="text-white font-semibold">{order.vehicle_info.transmission}</span>
                    </div>
                  )}

                  {/* Calculateur & Outil */}
                  <div className="pt-3 border-t border-[#A6A6A6]/20">
                    <Label className="text-[#A6A6A6] text-xs mb-2 block">CALCULATEUR / ECU & OUTIL</Label>
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Type d'ECU</span>
                      <span className="text-white font-semibold">{order.vehicle_info.ecu_type || 'N/A'}</span>
                    </div>
                    {order.vehicle_info.ecu_tool && (
                      <div className="flex justify-between mt-2">
                        <span className="text-[#A6A6A6]">Outil utilisé</span>
                        <span className="text-white font-semibold">{order.vehicle_info.ecu_tool}</span>
                      </div>
                    )}
                    {order.vehicle_info.file_type && (
                      <div className="flex justify-between mt-2">
                        <span className="text-[#A6A6A6]">Type fichier</span>
                        <span className="text-white font-semibold">{order.vehicle_info.file_type}</span>
                      </div>
                    )}
                    {order.vehicle_info.read_method && (
                      <div className="flex justify-between mt-2">
                        <span className="text-[#A6A6A6]">Méthode lecture</span>
                        <span className="text-white font-semibold">{order.vehicle_info.read_method}</span>
                      </div>
                    )}
                  </div>

                  {/* Modifications hardware */}
                  {order.vehicle_info.hardware_mods && order.vehicle_info.hardware_mods.length > 0 && (
                    <div className="pt-3 border-t border-[#A6A6A6]/20">
                      <Label className="text-[#A6A6A6] text-xs mb-2 block">MODIFICATIONS HARDWARE</Label>
                      <div className="flex flex-wrap gap-2">
                        {order.vehicle_info.hardware_mods.map((mod, i) => (
                          <Badge key={i} variant="outline" className="border-[#C00000]/50 text-white text-xs">
                            {mod}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Fichier Original */}
                  {order.original_file_name && (
                    <div className="pt-3 border-t border-[#A6A6A6]/20">
                      <Label className="text-[#A6A6A6] text-xs mb-2 block">FICHIER ORIGINAL (ECU)</Label>
                      <div className="flex items-center gap-2 bg-[#0a0a0a] p-3 rounded border border-[#A6A6A6]/10">
                        <FileText className="h-4 w-4 text-[#C00000]" />
                        <span className="text-white text-sm font-mono">{order.original_file_name}</span>
                      </div>
                    </div>
                  )}
                  
                  {order.notes && (
                    <div className="pt-3 border-t border-[#A6A6A6]/20">
                      <Label className="text-[#A6A6A6] text-xs">Notes Client</Label>
                      <p className="text-white text-sm mt-1">{order.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngineerOrderDetail;
